import { Component, Input, AfterViewInit } from '@angular/core';
import { ChartData, ChartOptions, ChartType, Chart } from 'chart.js';
import { BehaviorSubject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, AsyncPipe, TitleCasePipe } from '@angular/common';

@Component({
  selector: 'tl-ppr-chart',
  styleUrls: [
    './tl-ppr-chart.component.scss',
    '../tl-chart/tl-chart.component.scss',
  ],
  template: ` <div [ngClass]="classes">
    <div id="chart-header">
      <div class="title">
        {{ 'training_log.charts.ppr_title' | translate | titlecase }}
      </div>
      <div class="data">{{ progress | async }} {{ 'watts' | titlecase }}</div>
    </div>
    @if (data) {
      <div id="chart-container">
        <canvas id="pprChartRef">{{ chart }}</canvas>
      </div>
    } @else {
      <div class="data-error-template">
        {{ 'training_log.charts.data_error' | translate }}
      </div>
    }
  </div>`,
  standalone: true,
  imports: [NgClass, AsyncPipe, TitleCasePipe, TranslateModule],
})
export class TrainingLogPPRChartComponent implements AfterViewInit {
  chart: any;
  progress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  @Input() label!: string[];
  @Input() data!: number[];
  @Input() chartColor: string = '#428ff3';
  @Input() tension: number = 0.2;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  public get classes(): string[] {
    return [`chart-${this.size}`];
  }

  ngAfterViewInit(): void {
    const chartdata: ChartData<any> = {
      labels: this.label,
      datasets: [
        {
          data: this.data || [],
          borderColor: this.chartColor,
          backgroundColor: this.chartColor,
          hoverBackgroundColor: '#CCE2FF',
          tension: 0.2,
          fill: true,
          borderRadius: 4,
        },
      ],
    };

    const yMaxValue: number =
      Math.max(...this.data) > 100
        ? Math.round(Math.max(...this.data) / 100) * 100 + 100
        : Math.max(...this.data) > 50
          ? Math.round(Math.max(...this.data) / 10) * 10 + 10
          : Math.max(...this.data) + 1;

    const nonZeroData: number[] = (this.data || []).filter(
      (v: number) => v > 0
    );
    const avgPPR: number =
      nonZeroData && nonZeroData.length
        ? Math.round(
            nonZeroData.reduce((a: number, b: number) => a + b, 0) /
              nonZeroData.length
          )
        : 0;
    this.progress.next(avgPPR);

    // The initial options.
    const options: ChartOptions<any> = {
      defaultFontFamily: (Chart.defaults.font.family = "'Sk-Modernist-Bold'"),
      defaultFontSize: (Chart.defaults.font.size = 14),
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
      },
      plugins: {
        tooltip: {
          xAlign: 'center',
          yAlign: 'bottom',
          backgroundColor: this.chartColor,
          caretSize: 6,
          bodyColor: '#FFFFFF',
          mode: 'index',
          displayColors: false,
          callbacks: {
            label: (data: any) => {
              const value = data.label;
              this.progress.next(this.data[data.dataIndex]);
              if (value < 60) {
                return `${value}s`;
              } else if (value < 3600) {
                return `${(value / 60).toFixed(0)}m`;
              } else {
                return `${(value / 3600).toFixed(0)}h`;
              }
            },
            title: () => {
              return '';
            },
          },
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          border: {
            color: '#f4f4f4',
            width: 3,
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            autoSkipPadding: 30,
            sampleSize: 20,
            align: 'end',
            callback: (data: any) => {
              const value: number = Number.parseInt(this.label[data]);
              if (value < 60) {
                return `${value}s`;
              } else if (value < 3600) {
                console.log(`${(value / 60).toFixed(0)}m`);
                return `${(value / 60).toFixed(0)}m`;
              } else {
                return `${(value / 3600).toFixed(0)}h`;
              }
            },
          },
        },
        y: {
          stack: 'stack',
          max: yMaxValue,
          border: {
            display: false,
          },
          grid: {
            tickWidth: 0,
          },
          ticks: {
            crossAlign: 'center',
            padding: 8,
            callback: (data: any) => {
              return `${data}`;
            },
          },
        },
      },
      elements: {
        point: {
          radius: 0,
        },
      },
      onHover: (event, activeElements, chart) => {
        if (!activeElements || !activeElements.length) {
          this.progress.next(avgPPR);
        }
      },
    };

    this.chart = new Chart('pprChartRef', {
      type: 'bar',
      data: chartdata,
      options: options,
      plugins: [],
    });

    const canvas: HTMLCanvasElement = document.getElementById(
      'pprChartRef'
    ) as HTMLCanvasElement | null;
    if (canvas) {
      canvas.addEventListener('mouseleave', () => {
        this.progress.next(avgPPR);
      });
    }
  }
}
